import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import apiClient from '../client';
import type { UserResponse } from '../types';
import { useAuthStore } from '../../stores/auth-store';

/**
 * Retry policy for `/api/me`.
 *
 * A 403 is deterministic: the account has no user row in aid yet, and asking
 * again within the same second cannot change that. Retrying it only fired two
 * extra requests and delayed the honest "access pending" screen. Everything else
 * keeps the small retry, because those failures can be transient.
 */
export function shouldRetryMe(failureCount: number, error: unknown): boolean {
  if (axios.isAxiosError(error) && error.response?.status === 403) return false;
  return failureCount < 2;
}

export function useMe() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery<UserResponse>({
    queryKey: ['me'],
    queryFn: async () => {
      const { data } = await apiClient.get<UserResponse>('/me');
      return data;
    },
    enabled: isAuthenticated,
    retry: shouldRetryMe,
    // Revalidating on focus is what lets an account that was provisioned while
    // the session was open recover on its own.
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });
}
